import { diffLines } from 'diff/lib/diff/line.js';
import { diffWordsWithSpace } from 'diff/lib/diff/word.js';
import { DiffPart } from './diff-part';
import { PartType } from '../../../enums/part-type';

export class DiffField {
  private _lines?: DiffPart[][];
  private _label?: string;

  public get lines(): DiffPart[][] | undefined {
    return this._lines;
  }

  public get label(): string | undefined {
    return this._label;
  }

  public static create(label: string, left?: string, right?: string): DiffField {
    const field = new DiffField();
    field.buildDiff(label, left, right);
    return field;
  }

  public buildDiff(label: string, left?: string, right?: string): void {
    this.buildWordDiff(label, left, right);
  }

  public buildWordDiff(label: string, left?: string, right?: string): void {
    this._label = label;
    const diff = diffWordsWithSpace(left ?? '', right ?? '');
    const diffParts = diff.map((part) => this.createDiffPart(part.value, part.added, part.removed));

    if (diffParts.length == 0) {
      diffParts.push(this.createDiffPart('-', false, false));
    }

    this._lines = this.assembleLines(diffParts.flatMap((diffPart) => this.splitAtLineBreaks(diffPart)));
  }

  public buildLineDiff(label: string, left?: string, right?: string): void {
    this._label = label;

    const diff = diffLines(left ?? '', right ?? '', { stripTrailingCr: true });
    const lines = diff.flatMap((part) => {
      const lineParts = this.getLineParts(part.value);

      return lineParts.map((text) => [this.createDiffPart(text, part.added, part.removed)]);
    });

    this._lines = lines.length > 0 ? lines : [[this.createDiffPart('-', false, false)]];
  }

  private splitAtLineBreaks(diffPart: DiffPart): DiffPart[] {
    if (!diffPart.text.includes('\n')) {
      return [diffPart];
    }
    const diffParts = diffPart.text.split('\n').map((text) => {
      return {
        text: text,
        partType: diffPart.partType,
      } as DiffPart;
    });
    const partCount = diffParts.length;
    const result: DiffPart[] = [];
    for (let index = 0; index < partCount; index++) {
      result.push(diffParts[index]);

      if (index + 1 < partCount) {
        result.push({
          text: '',
          partType: PartType.LineBreak,
        } as DiffPart);
      }
    }
    return result;
  }

  private getLineParts(text: string): string[] {
    if (!text) {
      return [];
    }

    const lineParts = text.split('\n');

    if (text.endsWith('\n')) {
      lineParts.pop();
    }

    return lineParts;
  }

  private createDiffPart(text: string, added?: boolean, removed?: boolean): DiffPart {
    return {
      text,
      partType: added ? PartType.Added : removed ? PartType.Removed : PartType.Same,
    } as DiffPart;
  }

  private assembleLines(parts: DiffPart[]): DiffPart[][] {
    const result: DiffPart[][] = [];

    let line: DiffPart[] = [];

    for (const part of parts) {
      if (part.partType != PartType.LineBreak) {
        line.push(part);
        continue;
      }

      result.push(line);
      line = [];
    }

    result.push(line);
    return result;
  }
}
